import { forwardRef, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { StoreData } from "modules/main/types/storeData";
import { StoreCreationResume } from "../types/cartCreationResponse";
import { getStoreBdData } from "utils/storeDataManager";

import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Item, ItemContent, ItemDescription, ItemGroup, ItemMedia, ItemTitle } from "@/components/ui/item";
import { AutomatizationStep } from "../types/cartAutomatizationStatus";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Field, FieldLabel } from "@/components/ui/field";
import { Progress } from "@/components/ui/progress";
import Amount from "utils/amount";
import { LogIn, Megaphone, RefreshCw } from "lucide-react";
import { FinishProcessButton } from "./FinishProcessButton";
import posthog from "posthog-js";
import { SelectScenarioHandler } from "../types/scenarioSelection";


const LOGIN_REMOTE_BROWSER = "Login en la tienda";


/**
 * Anima un valor numérico desde su estado anterior hasta el nuevo target.
 * duration en ms (default 800ms).
 */
function useAnimatedNumber(target: number, duration = 800) {
    const [display, setDisplay] = useState(target);
    const rafRef = useRef<number | null>(null);
    const prevRef = useRef(target);

    useEffect(() => {
        const from = prevRef.current;
        const to = target;
        if (from === to) return;

        const start = performance.now();

        const tick = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = from + (to - from) * eased;
            setDisplay(current);
            if (progress < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                prevRef.current = to;
            }
        };

        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        };
    }, [target, duration]);

    return display;
}

interface CartCreationStoresProps extends React.HTMLAttributes<HTMLDivElement>{
    scenarioId: number;
    storeData: StoreData[],
    storeList: StoreCreationResume[];
    storeCreationStatus:AutomatizationStep[]
    setOpenBrowser: (store_id: number, token: string) => void;
    resumingStores?: Set<number>;
    onRetryResume?: (store_id: number) => void;
    onSelectScenario?: SelectScenarioHandler;
}

export const CartCreationStoreView = forwardRef<HTMLDivElement, CartCreationStoresProps>(({className,storeList,storeData,...props},ref) => {
    const statusPriority: Record<string, number> = {
        "LOGIN_REQUIRED": 0,
        "WAITING": 1,
        "PARTIAL": 2,
    };

    const getStatusPriority = (store_id: number) => {
        if (!props.storeCreationStatus || !Array.isArray(props.storeCreationStatus)) return 99;
        const status = props.storeCreationStatus.find(s => s.store_id === store_id)?.status ?? "";
        return statusPriority[status] ?? 99;
    };

    const sortedStoreList = [...storeList].sort((a, b) => getStatusPriority(a.store_id) - getStatusPriority(b.store_id));

    return(
    <ItemGroup ref={ref} className={cn(className,"w-full")}>
        <AnimatePresence initial={false}>
        {
            sortedStoreList.map((store) => {
                const storeInfo = getStoreBdData(store.store_id,storeData);
                const storeStatus = props.storeCreationStatus && Array.isArray(props.storeCreationStatus) ? props.storeCreationStatus.find(status => status.store_id === store.store_id) : undefined;
                return (
                    <motion.div
                        key={store.store_id}
                        layout
                        transition={{ type: "spring", stiffness: 400, damping: 35 }}
                    >
                        <CartStoreCreationCard
                            scenarioId={props.scenarioId}
                            store_id={store.store_id}
                            store_name={storeInfo?.name ?? ""} store_logo={storeInfo?.logo_url ?? ""} 
                            totalQuantityRequested={store.totalQuantityRequested ?? 0}
                            totalItemsRequested={store.totalItemsRequested ?? 0}
                            message={storeStatus?.message}
                            statusStep={storeStatus}
                            setOpenBrowser={props.setOpenBrowser}
                            isResuming={props.resumingStores?.has(store.store_id) ?? false}
                            onRetryResume={props.onRetryResume}
                            onSelectScenario={props.onSelectScenario}
                        />
                    </motion.div>
                );
            })
        }
        </AnimatePresence>
    </ItemGroup>
    )
});


interface CartStoreCreationCardProps extends React.HTMLAttributes<HTMLDivElement>, StoreCreationResume{
    scenarioId: number;
    store_name:string;
    store_logo:string;
    totalItemsRequested:number;
    totalQuantityRequested:number;
    statusStep?:AutomatizationStep;
    // token?:string;
    message?:string
    setOpenBrowser: (store_id: number, token: string) => void;
    isResuming?: boolean;
    onRetryResume?: (store_id: number) => void;
    onSelectScenario?: SelectScenarioHandler;
}

export const CartStoreCreationCard = forwardRef<HTMLDivElement, CartStoreCreationCardProps>(({className,store_id,store_name,store_logo,statusStep,message,...props},ref) => {
    const status = statusStep?.status ?? "WAITING";

    const totalItemsInCart = statusStep?.results ? statusStep.results.totalItemsInCart : 0;
    const totalQuantityInCart = statusStep?.results ? statusStep.results.totalQuantityInCart : 0;
    const percentage = statusStep?.percentage ?? 0;

    const token = statusStep?.token ?? "";

    const onOpenRemoteBrowserClick = () => {
        posthog.capture(LOGIN_REMOTE_BROWSER, {
            store_id,
        });
        props.setOpenBrowser(store_id, token);
    };

    const animatedPercentage = useAnimatedNumber(percentage, 900);
    const animatedQuantityInCart = useAnimatedNumber(totalQuantityInCart, 900);

    const statusProperties = {
        "PENDING":{
            text:"En fila para comenzar...",
            color:"text-muted-foreground",
            effect:"animate-pulse"
        },
        "STARTED":{
            text:"Conectando con la tienda...",
            color:"text-muted-foreground",
            effect:"animate-pulse"

        },
        "RESUMING":{
            text:"Retomando la conexión...",
            color:"text-muted-foreground",
            effect:"animate-pulse"
        },
        "WAITING":{
            text:"Pausado temporalmente...",
            color:"text-muted-foreground",
            effect: ""
        },
        "COMPLETED":{
            text:"¡Carrito listo!",
            color:"text-green-500",
            effect: ""
        },
        "FAILED":{
            text:"Tuvimos un problema",
            color:"text-red-500",
            effect: ""
        },
        "LOGIN_REQUIRED":{
            text:"Acceso necesario",
            color:"text-muted-foreground",
            effect: ""
        },
        "ADDRESS_NOT_SELECTED":{
            text:"Ingresa tu dirección",
            color:"text-muted-foreground",
            effect: ""
        },
        "PARTIAL":{
            text:"Completado parcialmente",
            color:"text-foreground",
            effect: ""
        },
        "REPLACING_ITEMS":{
            text:"Reemplazando productos sin stock...",
            color:"text-muted-foreground",
            effect:"animate-pulse"
        },
        "VALIDATING_LOGIN":{
            text:"Verificando tus datos...",
            color:"text-muted-foreground",
            effect:"animate-pulse"
        },
        "ADDING_TO_CART":{
            text:"Armando tu carrito...",
            color:"text-muted-foreground",
            effect:"animate-pulse"
        }


    }   
    const isWorkkinStatus = status === "STARTED" || status === "PENDING" || status === "WAITING";
    const missedItems = props.totalItemsRequested - totalItemsInCart;
    const showProductCounter = !["LOGIN_REQUIRED", "ADDRESS_NOT_SELECTED", "FAILED", "PENDING", "PARTIAL", "COMPLETED"].includes(status);

    return(    
    <Item ref={ref} className={cn(className,"")} variant={"outline"}>
        <ItemMedia>
            <Avatar className="size-11">
                <AvatarImage src={store_logo}/>
                <AvatarFallback>ER</AvatarFallback>
            </Avatar>
        </ItemMedia>
        <ItemContent>
            <ItemTitle className="flex flex-col gap-0 w-full items-start">
                <div className="flex flex-row w-full justify-between">
                    <span>{store_name}</span>
                {(status === "LOGIN_REQUIRED" || status === "ADDRESS_NOT_SELECTED") && (
                    props.isResuming ? (
                        <Button variant="default" size="xs" disabled>
                            <Spinner className="inline-block mr-1 h-3 w-3" />Procesando
                        </Button>
                    ) : (
                        <Button variant="default" size="xs" onClick={onOpenRemoteBrowserClick}><LogIn className="inline-block mr-1" />Ingresar</Button>
                    )
                )}
                {status === "FAILED" && (
                    props.isResuming ? (
                        <Button variant="default" size="xs" disabled>
                            <Spinner className="inline-block mr-1 h-3 w-3" />Procesando
                        </Button>
                    ) : (
                        <Button variant="default" size="xs" onClick={() => props.onRetryResume?.(store_id)}>
                            <RefreshCw className="inline-block mr-1 h-3 w-3" />Reintentar
                        </Button>
                    )
                )}
                {
                (status === "COMPLETED" || status === "PARTIAL") && (
                    <FinishProcessButton 
                        scenarioId={props.scenarioId}
                        status={statusStep!}
                        onSelectScenario={props.onSelectScenario}

                        />
                )
                }
                {showProductCounter && <span className="text-xs text-muted-foreground">{Math.round(animatedQuantityInCart)}/{props.totalQuantityRequested} productos</span>}                        
                </div>
            </ItemTitle>
            <ItemDescription
                className={cn(
                    {
                        "animate-pulse": isWorkkinStatus,
                    }
                )}
            >

                {/* {(status === "COMPLETED" || status === "PARTIAL") ? `${totalQuantityInCart}/${props.totalQuantityRequested} productos agregados al carrito.`:message} */}
            <Field className="w-full ">
                <Progress value={animatedPercentage * 100} id={`cart-creation-progress-${store_id}`}  barColor="#5CA273"/>
                <FieldLabel htmlFor={`cart-creation-progress-${store_id}`} className="flex justify-between">
                    <span className={cn("text-xs", statusProperties[status].color, statusProperties[status].effect)}>
                    {(status ==="PARTIAL")?(
                        <div className=""><Megaphone className="inline-block mr-1" />{missedItems} productos sin stock</div>
                    ):(statusProperties[status].text)
                    }                        
                    </span>
                    <Amount className="ml-4 text-sm" amount={animatedPercentage} formatStyle="percent"/>
                </FieldLabel>                
            </Field>
            </ItemDescription>
        </ItemContent>
    </Item>
);
});
